import React, { useState, useLayoutEffect } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Link } from 'gatsby';
import { window } from 'browser-monads';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import styled, { ThemeProvider } from 'styled-components';
import { useSwipeable } from 'react-swipeable';

import theme from '../../stylesheets/theme';

const getWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const resizeListener = () => {
    setWindowWidth(window.innerWidth);
  };

  useLayoutEffect(() => {
    window.addEventListener('resize', resizeListener);
    return () => window.removeEventListener('resize', resizeListener);
  }, []);

  return windowWidth;
};

const SeeMoreStyles = styled.div`
  margin-top: 50px;
  padding-top: 50px;
  width: 100vw;
  /* border-top: 2px solid ${(props) => props.theme.grey}; */
  display: flex;
  flex-direction: column;
  .see-more-title {
    margin-left: 50px;
  }
  .see-more-nav-container {
    width: 100vw;
    height: 100%;
    position: absolute;
  }
  .see-more-row-container {
    display: flex;
    flex-direction: row;
    margin: 25px 0 25px 0;
    position: relative;
    overflow: hidden;
    .see-more-nav-container {
      width: 100vw;
      position: absolute;
      display: flex;
      flex-direction: row;
      z-index: 3;
      pointer-events: none;
      .see-more-nav-button {
        color: ${(props) => props.theme.black};
        font-size: 18px;
        background: rgba(0, 0, 0, 0);
        pointer-events: auto;
        cursor: pointer;
        padding: 25px 50px 25px 10px;
        border: none;
        height: 100%;
        opacity: 0.7;
        @media (min-width: 650px) {
          padding: 25px 50px 25px 25px;
        }
        &:hover {
          opacity: 1;
          transition: all 0.3s ease;
        }
        &:nth-of-type(2) {
          padding: 25px 10px 25px 50px;
          @media (min-width: 650px) {
            padding: 25px 25px 25px 50px;
          }
        }
      }
      .see-more-nav-icon-container {
        border-radius: 50%;
        background: ${(props) => props.theme.white};
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.12),
          0px 8px 8px rgba(0, 0, 0, 0.24);
        transition: all 0.3s ease;
        .see-more-nav-icon {
        }
        .see-more-nav-icon-left {
          margin-right: 2px;
        }
        .see-more-nav-icon-right {
          margin-left: 2px;
        }
      }
      .see-more-left-button {
        margin-right: auto;
      }
      .see-more-right-button {
        margin-left: auto;
      }
    }
    .see-more-row {
      display: flex;
      flex-direction: row;
      width: 100vw;
      max-height: 50vh;
      max-width: 100vw;
      transition-duration: 200ms;
      .see-more-camp {
        display: flex;
        flex-direction: column;
        min-width: calc(90%);
        position: relative;
        margin: 0 0 0 5vw;
        @media (min-width: 650px) {
          min-width: calc(70%);
          margin: 0 0 0 25px;
        }
        @media (min-width: 950px) {
          min-width: calc(40%);
        }
        .see-more-camp-image {
          position: relative;
          min-width: 100%;
          min-height: 100%;
        }
        .see-more-camp-content-container {
          position: absolute;
          width: 100%;
          height: 100%;
          padding: 10%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.3);
          &:hover {
            background: rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
          }
          .see-more-camp-title {
            text-align: center;
            color: ${(props) => props.theme.white};
            max-width: 700px;
            margin-bottom: 10px;
          }
          .see-more-camp-dates {
            color: ${(props) => props.theme.white};
            margin: 5px auto 0;
            padding: 0 5px;
            text-align: center;
            display: none;
            @media (min-width: 350px) {
              display: flex;
            }
          }
        }
      }
    }
  }
`;

// eslint-disable-next-line react/prop-types
const SeeMore = ({ contentList, headline }) => {
  const [rowOffset, setRowOffset] = useState(0);
  const windowWidth = getWidth();

  const cardWidthMultiple = () => {
    // mirrors img width from media queries
    if (windowWidth > 950) return 0.4;
    if (windowWidth > 650) return 0.7;
    return 0.9;
  };

  const cardMargin = windowWidth > 650 ? 25 : windowWidth * 0.05;
  const cardWidth = windowWidth * cardWidthMultiple();

  // stop container from scrolling past last item
  const maxOffset = () => {
    // above 950 width (40% card)
    if (windowWidth > 950) {
      return (
        ((cardWidth + cardMargin) * (contentList.length - 2) -
          windowWidth * 0.2 +
          // adjust for visible margin
          cardMargin * 3) *
        -1
      );
    }
    // above 650 width (70% card)
    if (windowWidth > 650) {
      return (
        ((cardWidth + cardMargin) * (contentList.length - 1) -
          windowWidth * 0.3 +
          // adjust for visible margin
          cardMargin * 2) *
        -1
      );
    }
    // under 600 width (90% card, 5vw margins)
    return (
      ((cardWidth + cardMargin) * (contentList.length - 1) -
        windowWidth * 0.1 +
        // adjust for visible margin
        cardMargin * 2) *
      -1
    );
  };

  const setOffsetClick = (direction) => {
    if (direction === 'next') {
      if (rowOffset - cardWidth < maxOffset()) return setRowOffset(maxOffset());
      return setRowOffset(rowOffset - cardWidth - cardMargin);
    }

    if (direction === 'previous') {
      // if already at first, stop offsetting
      if (rowOffset + cardWidth + cardMargin >= 0) {
        return setRowOffset(0);
      }
      return setRowOffset(rowOffset + cardWidth + cardMargin);
    }
  };

  // swipable options
  const handlers = useSwipeable({
    onSwipedLeft: () => setOffsetClick('next'),
    onSwipedRight: () => setOffsetClick('previous'),
    trackMouse: true,
    preventDefaultTouchmoveEvent: false,
  });

  return (
    <ThemeProvider theme={theme}>
      <SeeMoreStyles
        // swipeable implementation
        {...handlers}
      >
        <h3 className="see-more-title">{headline}</h3>
        <div className="see-more-row-container">
          <div className="see-more-nav-container">
            <button
              type="button"
              className="see-more-left-button see-more-nav-button"
              onClick={() => setOffsetClick('previous')}
              style={{
                visibility: `${rowOffset === 0 ? 'hidden' : 'visible'}`,
              }}
            >
              <div className="see-more-nav-icon-container">
                <FontAwesomeIcon
                  className="see-more-nav-icon see-more-nav-icon-left"
                  icon={faChevronLeft}
                />
              </div>
            </button>
            <button
              type="button"
              className="see-more-right-icon see-more-nav-button"
              onClick={() => setOffsetClick('next')}
              style={{
                visibility: `${
                  rowOffset === maxOffset() ? 'hidden' : 'visible'
                }`,
              }}
            >
              <div className="see-more-nav-icon-container">
                <FontAwesomeIcon
                  className="see-more-nav-icon see-more-nav-icon-right"
                  icon={faChevronRight}
                />
              </div>
            </button>
          </div>
          <div
            className="see-more-row"
            style={{
              transform: `translate3d(calc(${rowOffset}px), 0px, 0px)`,
            }}
          >
            {contentList.map((item) => (
              <Link to={item.link} key={item.title} className="see-more-camp">
                <GatsbyImage
                  image={item.gatsbyImageData}
                  className="see-more-camp-image"
                  alt="image"
                />
                <div className="see-more-camp-content-container">
                  <h2 className="see-more-camp-title">{item.title}</h2>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </SeeMoreStyles>
    </ThemeProvider>
  );
};

export default SeeMore;
